import { useEffect, ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'

import { ChatCloseIcon } from '@/src/assets/icons'

import styles from './Modal.module.css'

interface ModalProps {
  isOpen: boolean
  onCancel: () => void
  children?: ReactNode
  className?: string
  title?: ReactNode
  footer?: ReactNode
}

export const Modal = ({ className, onCancel, isOpen, children, title, footer }: ModalProps) => {
  const modalRoot = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!onCancel) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    if (!modalRoot) return

    modalRoot.current = document.getElementById('modal-root')
  }, [])

  return (
    modalRoot?.current &&
    isOpen &&
    createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.15,
                scale: { type: 'spring', visualDuration: 0.15 },
              }}
              className={styles?.modalOverlay}
              onClick={onCancel}
            />

            <motion.div
              key="modal-content"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.15,
                scale: { type: 'spring', visualDuration: 0.15 },
              }}
              className={clsx(styles?.modal, className)}
            >
              <header className={styles?.header}>
                {title && <h4 className={styles?.title}>{title}</h4>}

                {onCancel && (
                  <button className={styles?.modalClose} onClick={onCancel}>
                    <ChatCloseIcon />
                  </button>
                )}
              </header>

              <section className={styles?.modalWindow}>{children}</section>

              {footer && <div className={styles?.footer}>{footer}</div>}
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      modalRoot?.current,
    )
  )
}
